import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type {
    SensorGroupDto,
    SensorGroupsDto,
} from "@station/data/dto/sensor-group.dto";
import {
    SensorGroupDtoSchema,
    SensorGroupsDtoSchema,
} from "@station/data/dto/sensor-group.dto";
import {
    SensorGroup,
    SensorGroups,
} from "@station/domain/models/sensor-group.model";
import {
    FormatEnum,
    Sensor,
    SeriesTypeEnum,
} from "@station/domain/models/sensor.model";
import { injectable } from "inversify";
import type { Result } from "neverthrow";
import { err } from "neverthrow";

@injectable()
export class SensorGroupMapper {
    map(
        sensorGroupDto: SensorGroupDto,
    ): Result<SensorGroup, ModelError<typeof SensorGroup>> {
        const sensorGroupDtoParsed =
            SensorGroupDtoSchema.safeParse(sensorGroupDto);

        if (!sensorGroupDtoParsed.success) {
            console.error(sensorGroupDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const { variables } = sensorGroupDto;

        const mappedSensors = variables.map((sensorDto) => {
            const sensorProps = {
                id: sensorDto.id,
                code:
                    sensorDto.code === undefined || sensorDto.code === ""
                        ? null
                        : sensorDto.code,
                name: sensorDto.name,
                extendedName: sensorDto.extended_name ?? null,
                format: sensorDto.format ? FormatEnum[sensorDto.format] : null,
                allowAccumulatedValues:
                    sensorDto.allow_accumulated_values ?? false,
                icon: sensorDto.icon ?? null,
                symbol: sensorDto.symbol ?? null,
                graphable: sensorDto.graphable ?? false,
                datable: sensorDto.datable ?? false,
                color: sensorDto.color ?? null,
                minThreshold: sensorDto.min_threshold ?? null,
                maxThreshold: sensorDto.max_threshold ?? null,
                goodThreshold: sensorDto.good_threshold ?? false,
                showTendency: sensorDto.show_tendency ?? false,
                allowReportTendency: sensorDto.allow_report_tendency ?? false,
                allowReportSum: sensorDto.allow_report_sum ?? false,
                seriesType: sensorDto.series_type
                    ? SeriesTypeEnum[sensorDto.series_type]
                    : null,
                variablePlotBands: sensorDto.variable_plot_bands
                    ? sensorDto.variable_plot_bands.map((plotBandDto) => ({
                          color: plotBandDto.color,
                          label: plotBandDto.label,
                          from: plotBandDto.from,
                          to: plotBandDto.to,
                      }))
                    : [],
                customPlotBands: sensorDto.custom_plot_bands
                    ? sensorDto.custom_plot_bands.map((plotBandDto) => ({
                          color: plotBandDto.color,
                          label: plotBandDto.label,
                          from: plotBandDto.from,
                          to: plotBandDto.to,
                      }))
                    : [],
                measurementUnitId: sensorDto.measurement_unit_id ?? null,
                effectiveRange: sensorDto.effective_range ?? null,
            };

            return Sensor.createWithRecovery(sensorProps);
        });

        const validSensors = mappedSensors
            .map((result) =>
                result.match(
                    (sensor) => sensor,
                    (error) => {
                        if (error.recuperable) {
                            return error.instance;
                        }
                        console.error(
                            "Non-recoverable error mapping sensor:",
                            error,
                        );
                        return null;
                    },
                ),
            )
            .filter((sensor): sensor is Sensor => sensor !== null);

        return SensorGroup.createWithRecovery({
            id: sensorGroupDto.id,
            name: sensorGroupDto.name,
            publicName: sensorGroupDto.public_name ?? null,
            sensors: validSensors,
            yAxes: sensorGroupDto.y_axes.map((yAxesDto) => ({
                id: yAxesDto.id,
                label: yAxesDto.label,
                variableIds: yAxesDto.variable_ids,
            })),
        });
    }

    mapList(sensorGroupsDto: SensorGroupsDto): SensorGroups {
        const sensorGroupsDtoParsed =
            SensorGroupsDtoSchema.safeParse(sensorGroupsDto);

        if (!sensorGroupsDtoParsed.success) {
            console.error(sensorGroupsDtoParsed.error.issues);
            return SensorGroups.empty();
        }

        const sensorGroups = sensorGroupsDto.mapNotNull((sensorGroupDto) =>
            this.map(sensorGroupDto).match(
                (sensorGroup) => sensorGroup,
                (error) => {
                    if (!error.recuperable) {
                        console.error(
                            "Non-recoverable error mapping sensor group:",
                            error,
                        );
                    }
                    return null;
                },
            ),
        );

        return SensorGroups.create({
            sensorGroups,
            count: sensorGroups.length,
        }).unwrapOr(SensorGroups.empty());
    }
}
